import { now, parseToDateTime } from '@calendly/datetime'
import { isEmpty } from 'lodash-es'

import { startOfUsWeekSunday } from 'services/us_week_start'

import { Periods, SchedulingMethods, STATUS_MAP } from './constants'

/** GraphQL filter datetime strings; match legacy moment().format('YYYY-MM-DDTHH:mm:ssZ') style offsets. */
const toGraphqlDateTime = (dt) => dt.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ")

const endOfUsWeekSaturday = (dt) => startOfUsWeekSunday(dt).plus({ days: 6 }).endOf('day')

const addDateRangeFilter = (startDate, endDate, past, forCount) => {
  const today = now().startOf('day')
  const todayStr = toGraphqlDateTime(today)
  const endOfTodayStr = toGraphqlDateTime(today.endOf('day'))
  const startDt = parseToDateTime(startDate)
  const endDt = parseToDateTime(endDate)
  const formattedStartDate = toGraphqlDateTime(startDt)
  const formattedEndDate = toGraphqlDateTime(endDt.endOf('day'))

  if (forCount) return `start_time: {operation: BETWEEN, start: "${formattedStartDate}", end: "${formattedEndDate}"}`

  if (past) {
    const actualEndDate = endDt.startOf('day') < today ? formattedEndDate : endOfTodayStr
    return `start_time: {operation: BETWEEN, start: "${formattedStartDate}", end: "${actualEndDate}"}`
  }
  const actualStartDate = startDt > today ? formattedStartDate : todayStr
  return `start_time: {operation: BETWEEN, start: "${actualStartDate}", end: "${formattedEndDate}"}`
}

const buildPeriodFilter = ({ period, past, startDate, endDate, today, forCount }) => {
  switch (period) {
    case Periods.UPCOMING: {
      if (forCount) return ''
      const startOfTodayStr = toGraphqlDateTime(now().startOf('day'))
      const endOfTodayStr = toGraphqlDateTime(now().endOf('day'))
      if (today) {
        return `start_time: {operation: BETWEEN, start: "${startOfTodayStr}", end: "${endOfTodayStr}"}`
      }
      if (past) {
        return `start_time: {operation: LT, start: "${startOfTodayStr}"}`
      }
      return `start_time: {operation: GTE, start: "${endOfTodayStr}"}`
    }
    case Periods.FIXED:
      return addDateRangeFilter(startDate, endDate, past, forCount)
    case Periods.THIS_WEEK: {
      const z = now()
      const startOfWeek = startOfUsWeekSunday(z)
      const endOfWeek = endOfUsWeekSaturday(z)
      return addDateRangeFilter(startOfWeek, endOfWeek, past, forCount)
    }
    case Periods.THIS_MONTH: {
      const startOfMonth = now().startOf('month')
      const endOfMonth = now().endOf('month')
      return addDateRangeFilter(startOfMonth, endOfMonth, past, forCount)
    }
    case Periods.THIS_YEAR: {
      const startOfYear = now().startOf('year')
      const endOfYear = now().endOf('year')
      return addDateRangeFilter(startOfYear, endOfYear, past, forCount)
    }
    case Periods.TODAY: {
      const startOfDay = now().startOf('day')
      const endOfDay = now().endOf('day')
      return addDateRangeFilter(startOfDay, endOfDay, past, forCount)
    }
    default:
      return ''
  }
}

const buildStringListFilter = (key, values) => {
  if (!isEmpty(values)) {
    return `${key}: [${values.map((value) => `"${value}"`).join(', ')}]`
  }

  return ''
}

const buildSchedulingMethodsFilter = ({ schedulingMethods }) => {
  if (isEmpty(schedulingMethods)) {
    return ''
  }

  const kinds = []

  if (schedulingMethods.includes(SchedulingMethods.CALENDLY)) {
    kinds.push('CALENDLY')
  }

  if (schedulingMethods.includes(SchedulingMethods.GOOGLE)) {
    kinds.push('GOOGLE')
  }

  if (schedulingMethods.includes(SchedulingMethods.OUTLOOK)) {
    kinds.push('OUTLOOK')
  }

  if (kinds.length === 0) {
    return ''
  }

  return `kinds: [${kinds.join(', ')}]`
}

const buildStatusFilter = ({ statusIds }) => {
  if (!statusIds || statusIds.length === 0) {
    return ''
  }

  const statusValues = statusIds
    .map((s) => STATUS_MAP[s])
    .filter(Boolean)

  if (statusValues.length === 0) {
    return ''
  }

  if (statusValues.length === 1) {
    return `status: ${statusValues[0]}`
  }

  return `statuses: [${statusValues.join(', ')}]`
}

const buildUuidFilter = ({ uuid }) => {
  if (uuid) {
    return `ids: ["${uuid}"]`
  }

  return ''
}

const buildInviteeEmailFilter = ({ inviteeEmails }) => buildStringListFilter('invitee_emails', inviteeEmails)
const buildEventTypeFilter = ({ eventTypeIds }) => buildStringListFilter('event_type_uuids', eventTypeIds)
const buildPackageUuidsFilter = ({ packageUuids }) => buildStringListFilter('package_uuids', packageUuids)

const buildUserUuidsFilter = ({ userUuids }) => {
  if (!userUuids || userUuids.length === 0) {
    return ''
  }

  return `user_uuids: [${userUuids.map((uuid) => `"${uuid}"`).join(', ')}]`
}

const buildTeamUuidsFilter = ({ teamUuids }) => {
  if (!teamUuids || teamUuids.length === 0) {
    return ''
  }

  return `team_uuids: [${teamUuids.map((uuid) => `"${uuid}"`).join(', ')}]`
}

const buildUserIdsFilter = ({ userIds }) => {
  if (!userIds || userIds.length === 0) {
    return ''
  }

  return `user_uuids: [${userIds.map((uuid) => `"${uuid}"`).join(', ')}]`
}

const buildTeamIdsFilter = ({ teamIds }) => {
  if (!teamIds || teamIds.length === 0) {
    return ''
  }

  return `team_uuids: [${teamIds.map((uuid) => `"${uuid}"`).join(', ')}]`
}

const buildOwnerTypeFilter = ({ ownerType }) => {
  if (ownerType) {
    return `owner_type: ${ownerType}`
  }

  return ''
}

const buildIsOrganizerFilter = () => 'is_organizer: false'

const buildIncludeNotetakerIneligibleExternalEventsFilter = () => 'include_notetaker_ineligible_external_events: true'

const buildRoleFilter = ({ role }) => {
  if (!role || role.length === 0) {
    return ''
  }

  return `role: [${role.join(', ')}]`
}

const buildMeetingIntelligenceFilter = ({ meetingIntelligenceStatus }) => {
  if (meetingIntelligenceStatus) {
    return `meeting_intelligence_status: ${meetingIntelligenceStatus}`
  }

  return ''
}

const buildUtmFilters = ({ utmCampaign, utmContent, utmMedium, utmSource, utmTerm }) => {
  const filters = [
    buildStringListFilter('utm_campaign', utmCampaign),
    buildStringListFilter('utm_content', utmContent),
    buildStringListFilter('utm_medium', utmMedium),
    buildStringListFilter('utm_source', utmSource),
    buildStringListFilter('utm_term', utmTerm)
  ]

  return filters.filter(Boolean).join(', ')
}

// ScopeType enum does not include all_users_and_teams; omit scope filter for this value
const INVALID_SCOPE_TYPES = ['all_users_and_teams']

const buildScopeFilters = ({ scopeType, scopeId }) => {
  let returnValue = ''

  if (scopeType && !INVALID_SCOPE_TYPES.includes(scopeType)) {
    returnValue = `scope_type: ${scopeType}`
  }

  if (scopeId) {
    const value = `scope_id: ${scopeId}`
    returnValue = returnValue ? `${returnValue}, ${value}` : value
  }

  return returnValue
}

const buildSearchKeywordFilter = ({ searchKeyword }) => {
  if (searchKeyword) {
    return `search_keyword: "${searchKeyword}"`
  }

  return ''
}

const buildRecapScopeFilter = ({ recapScope }) => {
  if (!recapScope) {
    return ''
  }

  return `recap_scope: ${recapScope}`
}

export const buildFilterString = (variables) => {
  const filters = []

  filters.push(buildPeriodFilter(variables))
  filters.push(buildInviteeEmailFilter(variables))
  filters.push(buildEventTypeFilter(variables))
  filters.push(buildPackageUuidsFilter(variables))
  filters.push(buildSchedulingMethodsFilter(variables))
  filters.push(buildStatusFilter(variables))
  filters.push(buildUuidFilter(variables))
  filters.push(buildUserUuidsFilter(variables))
  filters.push(buildTeamUuidsFilter(variables))
  // Needed because useFilterParamsWithUuids actually returns ids like build ET filter above
  filters.push(buildUserIdsFilter(variables))
  filters.push(buildTeamIdsFilter(variables))

  filters.push(buildRoleFilter(variables))
  filters.push(buildOwnerTypeFilter(variables))
  filters.push(buildIsOrganizerFilter())
  filters.push(buildMeetingIntelligenceFilter(variables))
  filters.push(buildIncludeNotetakerIneligibleExternalEventsFilter())
  filters.push(buildUtmFilters(variables))
  filters.push(buildScopeFilters(variables))
  filters.push(buildSearchKeywordFilter(variables))
  filters.push(buildRecapScopeFilter(variables))

  return filters.filter((v) => Boolean(v)).join(', ')
}
